import React, { ReactNode } from 'react';
import {
  Button,
  ButtonColor,
  ButtonSize,
  ItemsList,
  LoadingAnimation, PopupContext,
  SimpleBlock,
  Text, TextLink,
  usePopupManager
} from 'ui-builder';
import {
  EmailDomain,
  SENDER_EMAIL_UPDATED,
  SenderEmail, TeamPermission,
  useAuthenticationContextRequired,
  useEmailDomainsService,
  useSenderEmailService,
  VerificationStatus
} from 'nekst-api';
import { useDataLoaderNew } from 'data-loader';
import { ConfirmDeletePopup, usePopupFormManager } from 'features/nekst-widgets';
import { SenderEmailForm } from './SenderEmailForm';
import { useAuthorizationChecker } from 'authorization-scope';

import styles from './SenderEmail.module.scss';

function NoSenderEmailView(
  props: {
    personId: number,
  }
) {
  return (
    <>
      <SimpleBlock className={styles.buttonsContainer}>
        <SenderEmailFormButton personId={props.personId} text="Add Email Address" />
      </SimpleBlock>
      <Text>
        Until a custom email address is added, email is being handled as follows:

        <ItemsList
          items={[
            'Emails will be sent from: noreply@nekst.com, with the sender’s First and Last Name displayed as the Sender’s Name.',
            <Text>Any <Text bold>replies</Text> to these emails will be sent directly to the
              personal Email Address of the sender</Text>,
          ]}
        />
      </Text>
    </>
  );
}

function SenderEmailFormButton(
  props: {
    personId: number,
    text: string
  }
) {
  const popupFormManager = usePopupFormManager();

  return (
    <Button
      text={props.text}
      onClick={() => {
        popupFormManager.openForm(<SenderEmailForm personId={props.personId} />);
      }}
      color={ButtonColor.GREEN}
      size={ButtonSize.MEDIUM}
    />
  );
}

function RemoveSenderEmailButton(
  props: {
    personId: number
  }
) {
  const senderEmailService = useSenderEmailService();

  const popupManager = usePopupManager();

  return (
    <Button
      text="Delete Email"
      color={ButtonColor.RED}
      size={ButtonSize.MEDIUM}
      onClick={async () => {
        popupManager.openPopup(
          <ConfirmDeletePopup
            deleteFunc={() => senderEmailService.deletePersonSenderEmail(props.personId)}
            message="If you delete this email, emails will be sent from noreply@nekst.com."
          />
        );
      }}
    />
  );
}

function VerifiedEmailView(
  props: {
    personId: number,
    senderEmail: SenderEmail,
  }
) {
  return (
    <>
      <SimpleBlock className={styles.buttonsContainer}>
        <SenderEmailFormButton personId={props.personId} text="Update email address" />
        <RemoveSenderEmailButton personId={props.personId} />
      </SimpleBlock>
      <Text>
        Custom email is set up for sending, the system will use the following configuration:

        <ItemsList
          items={[
            <Text>Emails will be sent from: <Text bold>{props.senderEmail.email}</Text></Text>
          ]}
        />
      </Text>

    </>
  );
}

export function TeamMemberSenderEmailWidget(
  props: {
    personId: number
  }
) {
  const senderEmailService = useSenderEmailService();
  const emailDomainsService = useEmailDomainsService();

  const {
    data: senderEmail,
    loading
  } = useDataLoaderNew(
    () => senderEmailService.getPersonSenderEmail(props.personId),
    {
      dependencies: [props.personId],
      events: [SENDER_EMAIL_UPDATED],
    }
  );

  const {
    data: emailDomains,
  } = useDataLoaderNew(
    () => emailDomainsService.getTeamDomains(),
    {
      dependencies: [],
      events: []
    }
  );

  if (loading || !emailDomains) {
    return (<LoadingAnimation />);
  } else {
    return senderEmail
      ? (
        <VerifiedEmailView
          senderEmail={senderEmail}
          personId={props.personId}
        />
      )
      : (
        <NoSenderEmailView
          personId={props.personId}
        />
      );
  }
}

export function TeamMemberSenderEmailWidgetWithWrapper(
  props: {
    openEmailConfigurationPage: () => void,
    personId: number,
  }
) {
  return (
    <VerifiedDomainsContext openEmailConfigurationPage={props.openEmailConfigurationPage}>
      <TeamMemberSenderEmailWidget personId={props.personId} />
    </VerifiedDomainsContext>
  );
}

const VerifiedDomainsReactContext = React.createContext<EmailDomain[]>([]);

export function useVerifiedDomainsContext() {
  return React.useContext(VerifiedDomainsReactContext);
}

export function VerifiedDomainsContext(
  props: {
    children: ReactNode,
    openEmailConfigurationPage: () => void
  }
) {
  const emailDomainsService = useEmailDomainsService();

  const { isGranted } = useAuthorizationChecker();

  const {
    data: emailDomains,
  } = useDataLoaderNew(
    () => emailDomainsService.getTeamDomains(),
    {
      dependencies: [],
      events: []
    }
  );

  if (emailDomains) {
    const verifiedDomains = emailDomains
      .filter((item) => item.status === VerificationStatus.VERIFIED);

    if (verifiedDomains.length > 0) {
      return (
        <VerifiedDomainsReactContext.Provider
          value={verifiedDomains}
        >
          <PopupContext>
            {props.children}
          </PopupContext>
        </VerifiedDomainsReactContext.Provider>
      );
    } else {
      const canManage = isGranted(TeamPermission.MANAGE_EMAIL_CONFIGURATION);
      return (
        <Text>
          {canManage && (
            <>
              Click
              {' '}
              <TextLink
                onClick={() => {
                  props.openEmailConfigurationPage();
                }}
              >here</TextLink>
              {' '}
              to first configure your sending domain.
            </>
          )}
          {!canManage && (<>
            Contact your team owner to configure email domain first.
          </>)}
        </Text>
      );
    }
  } else {
    return (<LoadingAnimation />);
  }
}

export function CurrentUserSenderEmailWidget(
  props: {
    openEmailConfigurationPage: () => void
    withTitle?: boolean
  }
) {
  const authenticationContext = useAuthenticationContextRequired();

  return (
    <TeamMemberSenderEmailWidgetWithWrapper
      openEmailConfigurationPage={props.openEmailConfigurationPage}
      personId={authenticationContext.user.id}
    />
  );
}
